import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { CatalogRatioGroupDataFilterInput, DismantlingCoefficientTypeEnum, RatioTypeEnum } from 'gql/graphql';

import { useEffect, useMemo } from 'react';

import { RatiosTypeEnum } from '../../CatalogRatios';
import { catalogRatioGroupsGql } from '../catalogRatiosQueries';

interface Props {
    ratioTypeId?: string | number;
    groupsEffect: boolean;
    dismantlingCoef?: DismantlingCoefficientTypeEnum;
    parentId?: string | null;
}

export const useGroupsData = ({ ratioTypeId, groupsEffect, dismantlingCoef, parentId }: Props) => {
    const variables = useMemo(
        () => ({
            ratioType: ratioTypeId as RatioTypeEnum,
            ...(dismantlingCoef || parentId
                ? {
                      where: {
                          and: [
                              dismantlingCoef ? { groupType: { eq: dismantlingCoef } } : null,
                              parentId !== undefined ? { parentId: { eq: parentId } } : null,
                          ].filter(Boolean),
                      } as CatalogRatioGroupDataFilterInput,
                  }
                : {}),
        }),
        [ratioTypeId, dismantlingCoef, parentId],
    );

    const [groupsDataReset] = useResetStoreData({
        requestId: 'catalogRatioGroups',
    });

    useEffect(
        () => () => {
            groupsDataReset(undefined);
        },
        [groupsDataReset],
    );

    const [groupsData, groupsStatus] = useRequest({
        requestId: 'catalogRatioGroups',
        query: catalogRatioGroupsGql,
        variables,
        withEffect:
            groupsEffect && Boolean(ratioTypeId) && Object.keys(RatiosTypeEnum).includes(ratioTypeId as RatiosTypeEnum),
    });

    return useMemo(
        () => ({
            groupsData: ratioTypeId ? groupsData?.catalogRatioGroups ?? [] : [],
            groupsStatus,
        }),
        [groupsData?.catalogRatioGroups, groupsStatus, ratioTypeId],
    );
};
